import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Phone, Mail, FileText, Link2, Clock,
  MessageSquare, Loader2, X, ExternalLink,
  PhoneIncoming, PhoneOutgoing, Tag
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { API } from "../config/api";

const ACTIVITY_ICONS = {
  attendance_note: Phone,
  telephone_message: MessageSquare,
  matter_link: Link2,
  email_filed: Mail,
};

const ACTIVITY_DOT_COLORS = {
  attendance_note: "bg-blue-500",
  telephone_message: "bg-status-amber",
  matter_link: "bg-teal",
  email_filed: "bg-purple-500",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) +
      " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function ContactActivityPanel({ contactId, open, onClose, onNavigateToFull }) {
  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        axios.get(`${API}/contacts/${contactId}`, { withCredentials: true }),
        axios.get(`${API}/contacts/${contactId}/activity?limit=20`, { withCredentials: true }),
      ]);
      setContact(c.data);
      setActivities(a.data?.activities || []);
    } catch { setContact(null); setActivities([]); }
    setLoading(false);
  }, [contactId]);

  useEffect(() => { if (open && contactId) load(); }, [open, contactId, load]);

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-[380px] sm:w-[420px] bg-navy border-l border-navy-border p-0 overflow-hidden">
        <div className="flex flex-col h-full" data-testid="contact-activity-panel">
          {/* Header */}
          <SheetHeader className="p-4 border-b border-navy-border shrink-0">
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-teal animate-spin" />
                <SheetTitle className="text-sm text-slate-400">Loading...</SheetTitle>
              </div>
            ) : contact ? (
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal to-teal-dim flex items-center justify-center text-sm font-bold text-white shrink-0">
                    {(contact.display_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-sm font-semibold text-white truncate">{contact.display_name}</SheetTitle>
                    <div className="flex items-center gap-2 mt-0.5">
                      {contact.primary_phone && <span className="text-[10px] text-slate-500">{contact.primary_phone}</span>}
                      {contact.primary_email && <span className="text-[10px] text-slate-500">{contact.primary_email}</span>}
                    </div>
                  </div>
                </div>
                {contact.labels?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {contact.labels.map((l, i) => (
                      <span key={i} className="px-1.5 py-0.5 text-[9px] rounded-full bg-teal-dim/30 text-teal border border-teal/40">
                        {l.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
                {onNavigateToFull && (
                  <button onClick={() => onNavigateToFull(contactId)} className="flex items-center gap-1 text-[10px] text-teal hover:text-teal mt-2 transition-colors" data-testid="view-full-profile">
                    <ExternalLink className="w-2.5 h-2.5" />View full profile
                  </button>
                )}
              </div>
            ) : (
              <SheetTitle className="text-sm text-slate-500">Contact not found</SheetTitle>
            )}
          </SheetHeader>

          {/* Timeline */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 text-teal animate-spin" />
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-xs text-slate-500">No activity yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activities.map((item, i) => {
                  const Icon = ACTIVITY_ICONS[item.activity_type] || FileText;
                  const dotColor = ACTIVITY_DOT_COLORS[item.activity_type] || "bg-slate-500";
                  return (
                    <div key={i} className="flex gap-3" data-testid={`panel-activity-${i}`}>
                      <div className="flex flex-col items-center shrink-0 pt-1.5">
                        <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                        {i < activities.length - 1 && <div className="w-px flex-1 bg-navy-hover mt-1" />}
                      </div>
                      <div className="flex-1 min-w-0 pb-3">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Icon className="w-3 h-3 text-slate-500" />
                          <span className="text-[10px] text-slate-600">{formatDate(item.date)}</span>
                        </div>
                        <p className="text-xs text-white leading-relaxed">{item.summary}</p>
                        {item.matter_ref && (
                          <span className="inline-block mt-1 text-[9px] font-mono px-1 py-0.5 rounded bg-navy text-teal border border-teal/40">
                            {item.matter_ref}
                          </span>
                        )}
                        {(item.note_preview || item.message_preview) && (
                          <p className="text-[10px] text-slate-600 mt-1 line-clamp-2">{item.note_preview || item.message_preview}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
